/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
/* eslint-disable react/jsx-key */
import * as React from 'react';
import { useContext } from 'react';
import { min, get, camelCase } from 'lodash';
import { useTable, usePagination, useSortBy } from 'react-table';
import { Link } from 'react-router-dom';
import { css } from 'aphrodite';

import { Text, LoneIconButton } from '@blend/enterprise-components';

import { BlendBridge } from 'blb-types/blendBridge';
import { MeContext } from 'blb-contexts/Me';
import { getColumnConfigurations, PipelinePrincipalType } from './configs';
import { getStyles } from './styles';
import { pageSize as defaultPageSize } from './constants';

export namespace PipelineTable {
  export interface Props {
    loans: BlendBridge.API.Loan[];
  }
}

export const PipelineTable: React.FC<PipelineTable.Props> = ({ loans }) => {
  const me = useContext(MeContext);
  const styles = getStyles(me!.principalType);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    state: { pageIndex, pageSize },
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    page,
    rows,
    pageCount,
  } = useTable(
    {
      columns: getColumnConfigurations(me!.principalType as PipelinePrincipalType),
      data: loans,
      initialState: { pageSize: defaultPageSize },
    },
    useSortBy,
    usePagination,
  );
  const startRow = pageSize * pageIndex;
  const endRow = startRow + pageSize;
  return (
    <>
      <div className={css(styles.pipelineTableContainer)} id="it-PipelineTable-tableContainer">
        <table {...getTableProps({ className: css(styles.tableBody) })}>
          <thead>
            {headerGroups.map(headerGroup => (
              <tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column, columnIndex) => {
                  const isFirstColumn = columnIndex === 0;
                  return (
                    <th
                      {...column.getHeaderProps({
                        ...column.getSortByToggleProps(),
                        className: css(styles.tableHeader, isFirstColumn && styles.firstColumnIndent),
                      })}
                    >
                      <div className={css(styles.innerCellWrapper)}>{column.render('Header')}</div>
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody className={css(styles.pipelineTableContainer)} {...getTableBodyProps()}>
            {page.map((row, index) => {
              prepareRow(row);
              const isEvenRow = index % 2 === 0;
              return (
                <tr
                  {...row.getRowProps({
                    className: css(styles.tableRowBase, isEvenRow ? styles.tableRowEven : styles.tableRowOdd),
                  })}
                >
                  {row.cells.map((cell, cellIndex) => {
                    const isFirstCell = cellIndex === 0;
                    return (
                      <td
                        {...cell.getCellProps({
                          className: css(
                            isFirstCell && styles.firstColumnIndent,
                            get(styles, camelCase(cell.column.id)),
                          ),
                        })}
                      >
                        <div className={css(styles.innerCellWrapper)}>
                          <Link className={css(styles.navLink)} to={`/${row.original.loanId}/dashboard`}>
                            <div>{cell.render('Cell')}</div>
                          </Link>
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className={css(styles.pipelineTableFooter)} id="it-PipelineTable-paginationContainer">
        <div className={css(styles.footerInnerContainer)}>
          <div className={css(styles.footerElement)}>
            <Text>{`${startRow + 1} - ${min([endRow, rows.length])} of ${rows.length}`}</Text>
          </div>
          <div className={css(styles.footerElement)}>
            <LoneIconButton
              btnSize="sm"
              disabled={!canPreviousPage}
              icon="first_page"
              onClick={() => gotoPage(0)}
              theme="tertiary"
            />
          </div>
          <div className={css(styles.footerElement)}>
            <LoneIconButton
              btnSize="sm"
              disabled={!canPreviousPage}
              icon="chevron_left"
              onClick={() => previousPage()}
              theme="tertiary"
            />
          </div>
          <div className={css(styles.footerElement)}>
            <Text>{`Page ${pageIndex + 1} of ${pageOptions.length}`}</Text>
          </div>
          <div className={css(styles.footerElement)}>
            <LoneIconButton
              btnSize="sm"
              disabled={!canNextPage}
              icon="chevron_right"
              onClick={() => nextPage()}
              theme="tertiary"
            />
          </div>
          <div className={css(styles.footerElement)}>
            <LoneIconButton
              btnSize="sm"
              disabled={!canNextPage}
              icon="last_page"
              onClick={() => gotoPage(pageCount - 1)}
              theme="tertiary"
            />
          </div>
        </div>
      </div>
    </>
  );
};
